import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '@/services/api'
import { Card, Table, Pagination, Badge, Input, Select, Button, Modal } from '@/components/ui'
import { DEFAULT_PAGE_SIZE } from '@spoofio/shared'
import { formatDate } from '@/utils/format-date'
import type { AdminOrganizationResponse } from '@spoofio/shared/types'

type BadgeVariant = 'success' | 'danger' | 'warning' | 'primary' | 'gray'

const STATUS_BADGE_MAP: Record<string, { label: string; variant: BadgeVariant }> = {
  trial: { label: 'Trial', variant: 'primary' },
  trial_expired: { label: 'Trial Expired', variant: 'warning' },
  active: { label: 'Active', variant: 'success' },
  past_due: { label: 'Overdue', variant: 'danger' },
  canceled: { label: 'Canceled', variant: 'gray' },
  suspended: { label: 'Suspended', variant: 'warning' },
  inactive: { label: 'Inactive', variant: 'gray' },
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'trial', label: 'Trial' },
  { value: 'trial_expired', label: 'Trial Expired' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Overdue Payment' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'inactive', label: 'Inactive' },
]

const DEBOUNCE_MS = 300

export function AdminOrganizationsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const searchParam = searchParams.get('search') ?? ''
  const statusParam = searchParams.get('status') ?? ''
  const pageParam = parseInt(searchParams.get('page') ?? '1', 10)

  const [searchInput, setSearchInput] = useState(searchParam)
  const [debouncedSearch, setDebouncedSearch] = useState(searchParam)
  const [statusFilter, setStatusFilter] = useState(statusParam)
  const [currentPage, setCurrentPage] = useState(pageParam)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setCurrentPage(1)
    }, DEBOUNCE_MS)
    return () => { clearTimeout(timer) }
  }, [searchInput])

  // Sync URL params
  const syncParams = useCallback(
    (search: string, status: string, page: number) => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (status) params.set('status', status)
      if (page > 1) params.set('page', String(page))
      setSearchParams(params)
    },
    [setSearchParams]
  )

  useEffect(() => {
    syncParams(debouncedSearch, statusFilter, currentPage)
  }, [debouncedSearch, statusFilter, currentPage, syncParams])

  const offset = (currentPage - 1) * DEFAULT_PAGE_SIZE

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-organizations', debouncedSearch, statusFilter, offset],
    queryFn: () =>
      api.adminListOrganizations({
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        limit: DEFAULT_PAGE_SIZE,
        offset,
      }),
    staleTime: 30_000,
  })

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (org: AdminOrganizationResponse) => (
        <div>
          <p className="font-medium text-gray-900">{org.name}</p>
          <p className="text-xs text-gray-500">{org.slug}</p>
        </div>
      ),
    },
    {
      key: 'displayStatus',
      header: 'Status',
      render: (org: AdminOrganizationResponse) => {
        const badge = STATUS_BADGE_MAP[org.displayStatus] ?? {
          label: org.displayStatus,
          variant: 'gray' as BadgeVariant,
        }
        return <Badge variant={badge.variant}>{badge.label}</Badge>
      },
    },
    {
      key: 'planType',
      header: 'Plan',
      render: (org: AdminOrganizationResponse) => (
        <span className="capitalize">{org.planType}</span>
      ),
    },
    {
      key: 'canaryCount',
      header: 'Canaries',
      render: (org: AdminOrganizationResponse) => org.canaryCount,
    },
    {
      key: 'memberCount',
      header: 'Members',
      render: (org: AdminOrganizationResponse) => org.memberCount,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (org: AdminOrganizationResponse) => formatDate(org.createdAt),
    },
  ]

  const totalPages = data ? Math.ceil(data.total / DEFAULT_PAGE_SIZE) : 1

  const [showCreateModal, setShowCreateModal] = useState(false)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
        <Button onClick={() => { setShowCreateModal(true) }}>Create Organization</Button>
      </div>

      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <Input
            label="Search"
            placeholder="Search by name or slug..."
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value) }}
            className="w-64"
          />
          <Select
            label="Status"
            name="status"
            value={statusFilter}
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
            className="w-48"
          />
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load organizations</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={data?.data ?? []}
              keyExtractor={(org) => org.id}
              onRowClick={(org) => { navigate(`/admin/organizations/${org.id}`) }}
              isLoading={isLoading}
              emptyMessage="No organizations found"
            />
            {data && data.total > DEFAULT_PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={data.total}
                itemsPerPage={DEFAULT_PAGE_SIZE}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </Card>

      {showCreateModal && (
        <CreateOrganizationModal onClose={() => { setShowCreateModal(false) }} />
      )}
    </div>
  )
}

function toKebabCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function CreateOrganizationModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [ownerEmail, setOwnerEmail] = useState('')
  const [activateImmediately, setActivateImmediately] = useState(false)
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const createOrg = useMutation({
    mutationFn: () =>
      api.adminCreateOrganization({
        name,
        slug,
        ownerEmail: ownerEmail || undefined,
        activateImmediately,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-organizations'] })
      setCreatedApiKey(result.apiKey)
    },
  })

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
    if (!slugEdited) {
      setSlug(toKebabCase(e.target.value))
    }
  }

  const handleCopy = () => {
    if (createdApiKey) {
      void navigator.clipboard.writeText(createdApiKey)
      setCopied(true)
      setTimeout(() => { setCopied(false) }, 2000)
    }
  }

  // Show API key after successful creation
  if (createdApiKey) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        title="Organization Created"
        footer={
          <Button onClick={onClose}>Done</Button>
        }
      >
        <p className="text-sm text-gray-600 mb-3">
          Save this API key now. It will not be shown again.
        </p>
        <div className="flex items-center gap-2 bg-gray-50 rounded p-3 font-mono text-sm break-all">
          <span className="flex-1">{createdApiKey}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 text-primary-600 hover:text-primary-800 text-xs font-medium"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Create Organization"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            isLoading={createOrg.isPending}
            disabled={!name || !slug}
            onClick={() => { createOrg.mutate() }}
          >
            Create
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={handleNameChange}
          placeholder="e.g. Hillsdale College"
        />
        <Input
          label="Slug"
          value={slug}
          onChange={(e) => { setSlug(e.target.value); setSlugEdited(true) }}
          placeholder="e.g. hillsdale"
        />
        <Input
          label="Owner Email (optional)"
          type="email"
          value={ownerEmail}
          onChange={(e) => { setOwnerEmail(e.target.value) }}
          placeholder="e.g. admin@example.com"
        />
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={activateImmediately}
            onChange={(e) => { setActivateImmediately(e.target.checked) }}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">
            Activate immediately (skip trial, create Stripe subscription)
          </span>
        </label>
        {activateImmediately && (
          <p className="text-xs text-gray-500 ml-6">
            Creates a paid subscription and sets org to active status immediately.
          </p>
        )}
        {createOrg.error && (
          <p className="text-sm text-danger-600">
            {createOrg.error instanceof Error ? createOrg.error.message : 'Failed to create organization'}
          </p>
        )}
      </div>
    </Modal>
  )
}
